/**
 * useLeaderboard - Hook for fetching leaderboard data
 */

import { useState, useCallback, useEffect } from 'react';
import { useApi } from './useApi';
import { getLeaderboard, getUserRank } from '../api/services/leaderboardService';

/**
 * Hook to fetch and manage leaderboard data
 * @param {Object} options - Configuration options
 * @param {boolean} options.immediate - Fetch immediately on mount (default: true)
 * @param {number} options.limit - Number of entries to fetch (default: 10)
 * @param {number} options.page - Page number (default: 1)
 * @returns {Object} - { leaderboardData, totalItems, currentPage, totalPages, loading, error, refetch }
 */
export const useLeaderboard = (options = {}) => {
  const { 
    immediate = true, 
    limit = 10, 
    page: initialPage = 1,
    onSuccess,
    onError,
  } = options;

  const [currentPage, setCurrentPage] = useState(initialPage);

  const fetchLeaderboard = useCallback(
    () => getLeaderboard({ limit, page: currentPage }),
    [limit, currentPage]
  );

  const { data, loading, error, execute, reset } = useApi(fetchLeaderboard, {
    immediate: false,  // We'll control fetching via effects
    initialData: null,
    onSuccess,
    onError,
  });

  // Fetch on initial mount if immediate is true, then on any page change
  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [currentPage, immediate, execute]);

  // Extract pagination info from response
  const leaderboardData = data?.data || [];
  const totalItems = data?.total || leaderboardData.length || 0;
  // Use totalPages from API response if available, otherwise calculate
  const totalPages = data?.totalPages || Math.max(1, Math.ceil(totalItems / limit));

  return {
    leaderboardData,
    totalItems,
    currentPage,
    totalPages,
    loading,
    error,
    setPage: setCurrentPage,
    refetch: execute,
    reset,
  };
};

/**
 * Hook to fetch a specific user's rank
 * @param {string} userId - User ID or wallet address
 * @param {Object} options - Configuration options
 * @returns {Object} - { data, loading, error, refetch }
 */
export const useUserRank = (userId, options = {}) => {
  const { immediate = false, onSuccess, onError } = options;

  const fetchUserRank = useCallback(
    () => getUserRank(userId),
    [userId]
  );

  const { data, loading, error, execute, reset } = useApi(fetchUserRank, {
    immediate,
    initialData: null,
    onSuccess,
    onError,
  });

  return {
    userRank: data,
    loading,
    error,
    refetch: execute,
    reset,
  };
};

export default useLeaderboard;
